import React, { useState, useEffect, useRef } from "react";
import logo from "../assets/images/direct-money.jpeg";
import InstallPwaButton from "./InstallPwaButton";

export default function AppHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const root = document.documentElement; 
    if (darkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  const toggleTheme = () => setDarkMode((v) => !v);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    window.location.href = "/login";
  };

  return (
    <header
      className="sticky top-0 z-[100] w-full border-b border-black/5 dark:border-white/10
                 bg-white/70 dark:bg-slate-950/60 backdrop-blur-xl
                 px-4 py-3"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between">
        {/* Brand */}
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 overflow-hidden rounded-xl ring-1 ring-black/10 dark:ring-white/10">
            <img src={logo} alt="Direct Money Logo" className="h-full w-full object-cover" />
          </div>
          <div className="leading-tight">
            <h1 className="text-base font-semibold text-slate-900 dark:text-white">
              Direct Money
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Fast • Secure • Global
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3" ref={menuRef}>
          <InstallPwaButton />

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl
                       border border-black/10 dark:border-white/10
                       bg-white/60 dark:bg-white/5
                       hover:bg-white dark:hover:bg-white/10
                       transition"
            title={darkMode ? "Mode clair" : "Mode sombre"}
          >
            {darkMode ? (
              // Sun
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 3v2m0 14v2m9-9h-2M5 12H3m15.364 6.364-1.414-1.414M7.05 7.05 5.636 5.636m12.728 0-1.414 1.414M7.05 16.95l-1.414 1.414" />
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z" />
              </svg>
            ) : (
              // Moon
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-700 dark:text-slate-200" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M21 12.8A8.5 8.5 0 0 1 11.2 3a6.5 6.5 0 1 0 9.8 9.8Z" />
              </svg>
            )}
          </button>

          {/* Settings menu */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={menuOpen}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl
                         border border-black/10 dark:border-white/10
                         bg-white/60 dark:bg-white/5
                         hover:bg-white dark:hover:bg-white/10
                         transition"
              title="Menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-800 dark:text-slate-200" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M4 12h16M4 6h16M4 18h16" />
              </svg>
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-56 overflow-hidden rounded-2xl
                           border border-black/10 dark:border-white/10
                           bg-white dark:bg-slate-900 shadow-xl"
              >
                <button
                  onClick={() => {
                    alert("Profil utilisateur");
                    setMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 text-left text-sm
                             hover:bg-slate-50 dark:hover:bg-white/5
                             flex items-center gap-3"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 dark:bg-white/10">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-700 dark:text-slate-200" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M20 21a8 8 0 1 0-16 0" />
                      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
                    </svg>
                  </span>
                  <div>
                    <div className="font-medium text-slate-900 dark:text-white">Profil</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Infos & sécurité</div>
                  </div>
                </button>

                <div className="h-px bg-black/5 dark:bg-white/10" />

                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-3 text-left text-sm
                             hover:bg-red-50 dark:hover:bg-red-500/10
                             flex items-center gap-3 text-red-600 dark:text-red-400"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-red-100 dark:bg-red-500/10">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M10 17l5-5-5-5" />
                      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M15 12H3" />
                      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M21 3v18" />
                    </svg>
                  </span>
                  <div>
                    <div className="font-medium">Déconnexion</div>
                    <div className="text-xs opacity-80">Quitter votre session</div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
